import PropTypes from '~/lib/propTypes'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import Link from 'next/link'
import { useTranslation } from '~/components/TranslationContext'
import {
  H4,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  color,
  space,
  transition,
  device,
  fontWeight,
  radius,
  Button,
  SmallText,
  fontSize,
  Spinner,
} from '@ticketswap/solar'
import { WidePageLayout } from '~/components/PageLayout'
import NativeMenuDropdown from './NativeMenuDropdown'
import { getItems } from './menu-items'
import {
  AppStoreBadge,
  AppStoreLink,
  GooglePlayBadge,
  GooglePlayLink,
} from '~/components/AppStoreBadges'
import { ArrowDown, Play } from '@ticketswap/solar/icons'
import { useMutation } from '@apollo/client'
import { CHANGE_USER_LOCALE } from '~/graphql/mutations/changeUserLocale'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import HowItWorksVideoDialog from '~/components/SiteFooter/HowItWorksVideoDialog'
import { useRouter } from 'next/router'
import useStats from '~/hooks/useStats'
import priceCap from '~/utils/priceCap'
import { useIntercom } from '~/components/IntercomProvider'
import { useGeoInfo } from '~/components/GeoInfoContext'
import { useDeviceInfo } from '~/components/DeviceInfoContext'
import useLanguage from '~/hooks/useLanguage'

const Container = styled.footer`
  background-color: ${color.background};
  color: ${color.foreground};

  ${({ variant }) =>
    variant === 'default' &&
    css`
      border-block-start: 1px solid ${color.stroke};
    `}
`

const List = styled.ul`
  display: grid;
  grid-gap: ${space[4]};
`

const HorizontalList = styled(List)`
  display: flex;
  flex-direction: row;
`

const IconWrapper = styled.span`
  display: inline-block;
  margin-inline-end: ${space[8]};
  transform: translate3d(-2px, -2px, 0);
`

const navLinkStyles = css`
  color: ${color.foregroundMuted};
  transition: color ${transition};
  display: flex;
  white-space: nowrap;
  align-items: center;

  &:hover,
  &:focus {
    color: ${color.foreground};
  }
`

const NavLink = styled.a`
  ${navLinkStyles};

  padding-block: ${space[8]};
  padding-inline: 0;

  &:focus {
    box-shadow: none;
  }

  @media ${device.tablet} {
    padding-block: unset;
    padding-inline: unset;
  }
`

const StyledH4 = styled(H4)`
  margin-block-end: ${space[12]};
`

const Appendix = styled.div`
  border-block-start: 1px solid ${color.stroke};
  padding-block-start: ${space[32]};
  padding-block-end: ${space[32]};
`

const AppendixWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

const AppendixLegalSection = styled.div`
  display: none;

  @media ${device.tablet} {
    display: block;
    width: 50%;
  }
`

const Grid = styled.div`
  display: grid;
  grid-gap: ${space[32]};
  padding-block: ${space[32]};
  padding-inline: 0;

  @media ${device.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const GridElement = styled.div`
  > div:not(:first-of-type) {
    margin-block-start: ${space[32]};
  }
`

const NativeMenuDropdownWrapper = styled.div`
  @media ${device.tablet} {
    display: none;
  }
`

const PrimaryNavWrapper = styled.div`
  display: none;

  @media ${device.tablet} {
    display: block;
  }
`

const NavWrapper = styled.div`
  margin-block-end: ${space[32]};
`

const LegalNavItem = styled.li`
  margin-inline-end: ${space[24]};
`

const LanguageMenuList = styled(MenuList)`
  position: relative;
  margin-inline-end: ${space[4]};
`

const LanguageMenuButton = styled(MenuButton)`
  display: flex;

  span {
    color: ${color.foregroundMuted};
  }
`

const LanguageMenuIcon = styled.span`
  margin-inline-start: ${space[8]};
  margin-inline-start: ${space[8]};
`

const LanguageMenuItem = styled(MenuItem)`
  margin-block: 0;
  margin-inline: ${space[16]};
  border-radius: ${radius.sm};
`

const LanguageMenuText = styled.a`
  color: ${({ isCurrent }) => (isCurrent ? color.action : color.foregroundMuted)};
  font-weight: ${({ isCurrent }) => (isCurrent ? fontWeight.semiBold : fontWeight.regular)};
`

const AppStoreBadges = styled.div`
  display: grid;
  grid-gap: ${space[16]};
  grid-template-columns: auto 1fr;
  align-items: center;

  a {
    display: flex;
  }

  img {
    width: 100%;
    height: auto;
  }
`

const Rating = styled.div`
  display: flex;
  flex-direction: column;

  span {
    font-size: ${fontSize[12]};
    color: ${color.foregroundMuted};
  }
`

const SiteFooter = ({ variant = 'default' }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { countryCode } = useGeoInfo()
  const language = useLanguage()
  const { show: showIntercom } = useIntercom()
  const { loggedInUser } = useLoggedInUser()
  const deviceInfo = useDeviceInfo()
  const { isDesktop, isMobile, isIOS, isAndroid } = deviceInfo
  const { locale, asPath } = router
  const [changeUserLocale, { loading }] = useMutation(CHANGE_USER_LOCALE)
  const showAppStoreLink = isDesktop || (isMobile && isIOS)
  const showGooglePlayLink = isDesktop || (isMobile && isAndroid)
  const items = getItems(locale)
  const currentLocale =
    items.locales.find(localeItem => localeItem.value === locale) || items.locales[0]
  const { playStoreReviewsLabel, appStoreReviewsLabel, appStoreStars, playStoreStars } = useStats({
    t,
  })

  return (
    <Container data-hide-feedback-widget="true" variant={variant}>
      {variant === 'default' && (
        <WidePageLayout>
          <Grid>
            <GridElement>
              <StyledH4>{t('common:footer.primary.title')}</StyledH4>
              <nav aria-label="Primary navigation">
                <NativeMenuDropdownWrapper>
                  <NativeMenuDropdown t={t} items={items} />
                </NativeMenuDropdownWrapper>

                <PrimaryNavWrapper>
                  <List>
                    {items.primary.map(item => {
                      if (item?.locales && !item.locales.includes(locale)) return null

                      return (
                        <li key={item.title}>
                          {item.url ? (
                            <NavLink href={item.url}>
                              {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
                              {t(item.title)}
                            </NavLink>
                          ) : (
                            <Link prefetch={false} href={item.href} as={item.as} passHref>
                              <NavLink>
                                {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
                                {t(item.title)}
                              </NavLink>
                            </Link>
                          )}
                        </li>
                      )
                    })}
                  </List>
                </PrimaryNavWrapper>
              </nav>
            </GridElement>

            <GridElement>
              <div>
                <StyledH4>{t('common:footer.social.title')}</StyledH4>
                <nav aria-label="Social channels">
                  <List>
                    {items.social.map(item => (
                      <li key={item.title}>
                        <NavLink href={item.url}>{item.title}</NavLink>
                      </li>
                    ))}
                  </List>
                </nav>
              </div>

              <div>
                <StyledH4>{t('common:footer.help.title')}</StyledH4>
                <NavLink as="button" onClick={showIntercom}>
                  {t('common:footer.getHelp')}
                </NavLink>
              </div>
            </GridElement>

            <GridElement>
              {priceCap(locale, countryCode) === 120 && (
                <NavWrapper>
                  <HowItWorksVideoDialog t={t}>
                    {({ show }) => (
                      <Button
                        onClick={show}
                        variant="secondary"
                        leftAdornment={<Play size={16} />}
                        fullWidth
                      >
                        {t('common:footer.howItWorks.title')}
                      </Button>
                    )}
                  </HowItWorksVideoDialog>
                </NavWrapper>
              )}

              <AppStoreBadges>
                {showAppStoreLink && (
                  <>
                    <AppStoreLink trackingContext="SITE_FOOTER">
                      <AppStoreBadge />
                    </AppStoreLink>
                    <Rating>
                      <div>{appStoreStars}</div>
                      <SmallText>{appStoreReviewsLabel}</SmallText>
                    </Rating>
                  </>
                )}
                {showGooglePlayLink && (
                  <>
                    <GooglePlayLink trackingContext="SITE_FOOTER">
                      <GooglePlayBadge />
                    </GooglePlayLink>
                    <Rating>
                      <div>{playStoreStars}</div>
                      <SmallText>{playStoreReviewsLabel}</SmallText>
                    </Rating>
                  </>
                )}
              </AppStoreBadges>
            </GridElement>
          </Grid>
        </WidePageLayout>
      )}

      <Appendix>
        <WidePageLayout>
          <AppendixWrapper>
            <AppendixLegalSection>
              <nav aria-label="Legal navigation">
                <HorizontalList>
                  {items.legal
                    .filter(item => !item.languages || item.languages.includes(language))
                    .map(item => (
                      <LegalNavItem key={item.title}>
                        <Link prefetch={false} href={item.href} as={item.as} passHref>
                          <NavLink>{t(item.title)}</NavLink>
                        </Link>
                      </LegalNavItem>
                    ))}
                </HorizontalList>
              </nav>
            </AppendixLegalSection>
            <div>
              <Menu aria-busy={loading}>
                <LanguageMenuButton>
                  {t('common:footer.language.title')}:&nbsp; <span>{currentLocale.name}</span>
                  <LanguageMenuIcon>
                    {loading ? (
                      <Spinner color={color.foregroundMuted} size={24} />
                    ) : (
                      <ArrowDown
                        aria-label={t('common:footer.language.title')}
                        color={color.foregroundMuted}
                        size={12}
                      />
                    )}
                  </LanguageMenuIcon>
                </LanguageMenuButton>
                <LanguageMenuList>
                  {items.locales.map(itemLocale => (
                    <LanguageMenuItem
                      key={itemLocale.value}
                      onSelect={async () => {
                        if (loggedInUser) {
                          await changeUserLocale({
                            variables: {
                              input: {
                                locale: itemLocale.value,
                              },
                            },
                          })
                        }

                        router.push(asPath, undefined, { locale: itemLocale.value })
                      }}
                    >
                      <LanguageMenuText isCurrent={currentLocale.value === itemLocale.value}>
                        {itemLocale.name}
                      </LanguageMenuText>
                    </LanguageMenuItem>
                  ))}
                </LanguageMenuList>
              </Menu>
            </div>
          </AppendixWrapper>
        </WidePageLayout>
      </Appendix>
    </Container>
  )
}

SiteFooter.propTypes = {
  variant: PropTypes.oneOf(['default', 'appendix']),
}

export default SiteFooter
